// Dino roster + per-dino tuning. Sprite sheets are 576x24 = 24 frames @ 24x24.
// Frame layout (Arks DinoSprites, well-known pixel pack):
//   0-3   idle
//   4-9   run
//   10-12 kick
//   13-16 hurt
//   17-19 sneak
//   20    crouch
//   21    jump
//   22-23 idle alt (we ignore)

export const FRAME_W = 24;
export const FRAME_H = 24;
export const TOTAL_FRAMES = 24;

export const FRAMES = {
  idle: [0, 1, 2, 3],
  run: [4, 5, 6, 7, 8, 9],
  kick: [10, 11, 12],
  hurt: [13, 14, 15, 16],
  sneak: [17, 18, 19],
  crouch: [20],
  jump: [21],
} as const;

export type DinoId = 'doux' | 'mort' | 'tard' | 'vita';

export interface DinoStats {
  /** display name */
  name: string;
  /** background stripe color (matches ideas/maybeassets/misc/bg.png) */
  stripe: number;
  /** UI accent color */
  accent: number;
  /** name of this dino's home planet (shown on level select + cleared screen) */
  planet: string;
  /** max hit points */
  maxHp: number;
  /** horizontal run speed (px/sec) */
  speed: number;
  /** initial jump impulse (negative = up) */
  jumpVelocity: number;
  /** kick damage */
  kickDamage: number;
  /** flavor blurb on the select screen */
  blurb: string;
}

// Stripe colors are matched to each sprite sheet's actual dino color
// (doux = blue sprite, mort = red, tard = tan/orange, vita = green).
//
// jumpVelocity tuned with gravity 1100 for snappy ~70px hops (Doux):
//   apex = jv² / (2 * gravity)
//   time = -jv / gravity
export const DINOS: Record<DinoId, DinoStats> = {
  doux: {
    name: 'BOB',
    stripe: 0x009dff,
    accent: 0x004d8a,
    planet: 'GLACIER',
    maxHp: 4,
    speed: 130,
    jumpVelocity: -400, // apex 73px, time 0.36s
    kickDamage: 1, // gun damage
    blurb: 'Balanced. Forgiving arcs, steady gun.',
  },
  mort: {
    name: 'ROXY',
    stripe: 0xff1500,
    accent: 0x8a0a00,
    planet: 'INFERNO',
    maxHp: 3,
    speed: 160,
    jumpVelocity: -370, // apex 62px
    kickDamage: 2, // hits hard
    blurb: 'Fast & angry. Heavy gun, dies quick.',
  },
  tard: {
    name: 'BISCUIT',
    stripe: 0xffa600,
    accent: 0x8a5a00,
    planet: 'DUNES',
    maxHp: 5,
    speed: 110,
    jumpVelocity: -390, // apex 69px
    kickDamage: 1,
    blurb: 'Tanky. Slow, but soaks up punishment.',
  },
  vita: {
    name: 'SPROUT',
    stripe: 0xb6ff00,
    accent: 0x6b8a00,
    planet: 'CANOPY',
    maxHp: 4,
    speed: 140,
    jumpVelocity: -460, // apex 96px
    kickDamage: 1,
    blurb: 'Springy. Jumps highest, rapid trigger.',
  },
};

export const DINO_ORDER: DinoId[] = ['doux', 'mort', 'tard', 'vita'];

/**
 * Register the four shared dino animations for a given dino id.
 * We namespace per-dino so each dino has its own animation keys
 * driven off its own sprite sheet.
 */
export function registerDinoAnimations(scene: Phaser.Scene, id: DinoId) {
  const make = (key: string, frames: readonly number[], frameRate: number, repeat: number) => {
    const animKey = `${id}-${key}`;
    if (scene.anims.exists(animKey)) return;
    scene.anims.create({
      key: animKey,
      frames: scene.anims.generateFrameNumbers(`dino-${id}`, { frames: [...frames] }),
      frameRate,
      repeat,
    });
  };
  make('idle', FRAMES.idle, 6, -1);
  make('run', FRAMES.run, 12, -1);
  make('kick', FRAMES.kick, 14, 0);
  make('hurt', FRAMES.hurt, 10, 0);
  make('sneak', FRAMES.sneak, 8, -1);
  make('crouch', FRAMES.crouch, 1, 0);
  make('jump', FRAMES.jump, 1, 0);
}
